import { useState, useEffect } from 'react';
import { api } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  UtensilsCrossed, Plus, Edit2, Trash2, ChevronDown, ChevronRight,
  Loader2, X, GripVertical, Check, XCircle
} from 'lucide-react';
import { toast } from 'sonner';

const AdminMenu = () => {
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openCat, setOpenCat] = useState(null);
  const [showCatForm, setShowCatForm] = useState(false);
  const [showItemForm, setShowItemForm] = useState(false);
  const [editingCat, setEditingCat] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [catForm, setCatForm] = useState({ name: '', description: '', sort_order: 0 });
  const [itemForm, setItemForm] = useState({ name: '', description: '', price: 0, allergens: '', is_available: true, sort_order: 0, category_id: '' });
  const [saving, setSaving] = useState(false);

  const fetchAll = async () => {
    try {
      const [cRes, iRes] = await Promise.all([
        api.get('/resa/menu/categories'),
        api.get('/resa/menu/items'),
      ]);
      setCategories(cRes.data || []);
      setItems(iRes.data || []);
    } catch { /* empty */ }
  };

  useEffect(() => { fetchAll().finally(() => setLoading(false)); }, []);

  // Category CRUD
  const saveCat = async () => {
    setSaving(true);
    try {
      if (editingCat) {
        await api.put(`/resa/menu/categories/${editingCat}`, catForm);
        toast.success('Categorie modifiee');
      } else {
        await api.post('/resa/menu/categories', catForm);
        toast.success('Categorie creee');
      }
      setShowCatForm(false);
      setEditingCat(null);
      fetchAll();
    } catch { toast.error('Erreur'); }
    setSaving(false);
  };

  const deleteCat = async (id) => {
    if (!window.confirm('Supprimer cette categorie et ses plats ?')) return;
    try {
      await api.delete(`/resa/menu/categories/${id}`);
      toast.success('Categorie supprimee');
      fetchAll();
    } catch { toast.error('Erreur'); }
  };

  const openEditCat = (cat) => {
    setCatForm({ name: cat.name, description: cat.description || '', sort_order: cat.sort_order || 0 });
    setEditingCat(cat.id);
    setShowCatForm(true);
  };

  // Item CRUD
  const saveItem = async () => {
    setSaving(true);
    try {
      if (editingItem) {
        await api.put(`/resa/menu/items/${editingItem}`, itemForm);
        toast.success('Plat modifie');
      } else {
        await api.post('/resa/menu/items', itemForm);
        toast.success('Plat cree');
      }
      setShowItemForm(false);
      setEditingItem(null);
      fetchAll();
    } catch { toast.error('Erreur'); }
    setSaving(false);
  };

  const deleteItem = async (id) => {
    if (!window.confirm('Supprimer ce plat ?')) return;
    try {
      await api.delete(`/resa/menu/items/${id}`);
      toast.success('Plat supprime');
      fetchAll();
    } catch { toast.error('Erreur'); }
  };

  const openEditItem = (item) => {
    setItemForm({
      name: item.name, description: item.description || '', price: item.price || 0,
      allergens: item.allergens || '', is_available: item.is_available !== false,
      sort_order: item.sort_order || 0, category_id: item.category_id,
    });
    setEditingItem(item.id);
    setShowItemForm(true);
  };

  const openNewItem = (catId) => {
    setItemForm({ name: '', description: '', price: 0, allergens: '', is_available: true, sort_order: 0, category_id: catId });
    setEditingItem(null);
    setShowItemForm(true);
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[rgb(var(--lm-gold))]" /></div>;

  return (
    <div className="space-y-4" data-testid="admin-menu">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <UtensilsCrossed size={18} className="text-[rgb(var(--lm-gold))]" />
          <h1 className="text-lg font-title text-[rgb(var(--lm-text))]">Carte du restaurant</h1>
        </div>
        <Button onClick={() => { setCatForm({ name: '', description: '', sort_order: categories.length }); setEditingCat(null); setShowCatForm(true); }}
          size="sm" className="bg-[rgb(var(--lm-gold))] text-[rgb(var(--lm-bg))] hover:opacity-90 gap-1" data-testid="add-category">
          <Plus size={14} /> Categorie
        </Button>
      </div>

      {/* Categories accordion */}
      {categories.length === 0 ? (
        <div className="lm-card p-8 text-center"><p className="text-sm text-[rgb(var(--lm-text-muted))]">Aucune categorie. Creez-en une pour commencer.</p></div>
      ) : categories.map(cat => {
        const catItems = items.filter(i => i.category_id === cat.id);
        const isOpen = openCat === cat.id;
        return (
          <div key={cat.id} className="lm-card overflow-hidden" data-testid={`cat-${cat.id}`}>
            <div className="flex items-center p-3 cursor-pointer" onClick={() => setOpenCat(isOpen ? null : cat.id)}>
              {isOpen ? <ChevronDown size={14} className="text-[rgb(var(--lm-text-muted))] mr-2" /> : <ChevronRight size={14} className="text-[rgb(var(--lm-text-muted))] mr-2" />}
              <div className="flex-1">
                <p className="text-sm font-medium text-[rgb(var(--lm-text))]">{cat.name}</p>
                {cat.description && <p className="text-[10px] text-[rgb(var(--lm-text-muted))]">{cat.description}</p>}
              </div>
              <span className="text-[9px] text-[rgb(var(--lm-text-muted))] mr-3">{catItems.length} plats</span>
              <button onClick={(e) => { e.stopPropagation(); openEditCat(cat); }} className="p-1 text-[rgb(var(--lm-text-muted))] hover:text-[rgb(var(--lm-gold))]">
                <Edit2 size={12} />
              </button>
              <button onClick={(e) => { e.stopPropagation(); deleteCat(cat.id); }} className="p-1 text-[rgb(var(--lm-text-muted))] hover:text-[rgb(var(--lm-red))]">
                <Trash2 size={12} />
              </button>
            </div>

            {isOpen && (
              <div className="border-t border-[rgb(var(--lm-border)/0.3)] p-3 space-y-1">
                {catItems.map(item => (
                  <div key={item.id} className="flex items-center gap-2 py-2 border-b border-[rgb(var(--lm-border)/0.2)] last:border-0"
                    data-testid={`item-${item.id}`}>
                    <div className={`w-2 h-2 rounded-full shrink-0 ${item.is_available ? 'bg-green-400' : 'bg-red-400'}`} />
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm ${item.is_available ? 'text-[rgb(var(--lm-text))]' : 'text-[rgb(var(--lm-text-muted))] line-through'}`}>{item.name}</p>
                      {item.description && <p className="text-[9px] text-[rgb(var(--lm-text-muted))] truncate">{item.description}</p>}
                      {item.allergens && <p className="text-[8px] text-[rgb(var(--lm-gold))]">Allergenes: {item.allergens}</p>}
                    </div>
                    <span className="text-sm font-mono text-[rgb(var(--lm-gold))] shrink-0">{item.price?.toFixed(2)} &euro;</span>
                    <button onClick={() => openEditItem(item)} className="p-1 text-[rgb(var(--lm-text-muted))] hover:text-[rgb(var(--lm-gold))]"><Edit2 size={11} /></button>
                    <button onClick={() => deleteItem(item.id)} className="p-1 text-[rgb(var(--lm-text-muted))] hover:text-[rgb(var(--lm-red))]"><Trash2 size={11} /></button>
                  </div>
                ))}
                <button onClick={() => openNewItem(cat.id)}
                  className="w-full py-2 text-[10px] text-[rgb(var(--lm-gold))] hover:bg-[rgb(var(--lm-gold)/0.05)] transition-colors flex items-center justify-center gap-1"
                  data-testid={`add-item-${cat.id}`}>
                  <Plus size={11} /> Ajouter un plat
                </button>
              </div>
            )}
          </div>
        );
      })}

      {/* Category form modal */}
      {showCatForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={() => setShowCatForm(false)}>
          <div className="lm-card w-full max-w-sm mx-4 p-5" onClick={e => e.stopPropagation()} data-testid="cat-form-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-title text-[rgb(var(--lm-text))]">{editingCat ? 'Modifier la categorie' : 'Nouvelle categorie'}</h3>
              <button onClick={() => setShowCatForm(false)} className="text-[rgb(var(--lm-text-muted))]"><X size={16} /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-[9px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider block mb-1">Nom</label>
                <Input value={catForm.name} onChange={e => setCatForm(f => ({ ...f, name: e.target.value }))}
                  className="bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))]" placeholder="Entrees, Plats, Desserts..." data-testid="cat-name" />
              </div>
              <div>
                <label className="text-[9px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider block mb-1">Description</label>
                <Input value={catForm.description} onChange={e => setCatForm(f => ({ ...f, description: e.target.value }))}
                  className="bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))]" placeholder="Nos entrees du moment..." data-testid="cat-desc" />
              </div>
              <div className="flex gap-2 pt-2">
                <Button onClick={() => setShowCatForm(false)} variant="outline" className="flex-1 border-[rgb(var(--lm-border))]">Annuler</Button>
                <Button onClick={saveCat} disabled={saving} className="flex-1 bg-[rgb(var(--lm-gold))] text-[rgb(var(--lm-bg))]" data-testid="save-cat">
                  {saving ? <Loader2 size={14} className="animate-spin" /> : editingCat ? 'Modifier' : 'Creer'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Item form modal */}
      {showItemForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={() => setShowItemForm(false)}>
          <div className="lm-card w-full max-w-md mx-4 p-5" onClick={e => e.stopPropagation()} data-testid="item-form-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-title text-[rgb(var(--lm-text))]">{editingItem ? 'Modifier le plat' : 'Nouveau plat'}</h3>
              <button onClick={() => setShowItemForm(false)} className="text-[rgb(var(--lm-text-muted))]"><X size={16} /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-[9px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider block mb-1">Nom</label>
                <Input value={itemForm.name} onChange={e => setItemForm(f => ({ ...f, name: e.target.value }))}
                  className="bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))]" data-testid="item-name" />
              </div>
              <div>
                <label className="text-[9px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider block mb-1">Description</label>
                <Input value={itemForm.description} onChange={e => setItemForm(f => ({ ...f, description: e.target.value }))}
                  className="bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))]" data-testid="item-desc" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[9px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider block mb-1">Prix</label>
                  <Input type="number" step="0.50" min={0} value={itemForm.price}
                    onChange={e => setItemForm(f => ({ ...f, price: parseFloat(e.target.value) || 0 }))}
                    className="bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))] font-mono" data-testid="item-price" />
                </div>
                <div>
                  <label className="text-[9px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider block mb-1">Allergenes</label>
                  <Input value={itemForm.allergens} onChange={e => setItemForm(f => ({ ...f, allergens: e.target.value }))}
                    className="bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))]" placeholder="Lactose, Gluten..." data-testid="item-allergens" />
                </div>
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={itemForm.is_available} onChange={e => setItemForm(f => ({ ...f, is_available: e.target.checked }))} />
                <span className="text-xs text-[rgb(var(--lm-text-secondary))]">Disponible</span>
              </label>
              <div className="flex gap-2 pt-2">
                <Button onClick={() => setShowItemForm(false)} variant="outline" className="flex-1 border-[rgb(var(--lm-border))]">Annuler</Button>
                <Button onClick={saveItem} disabled={saving} className="flex-1 bg-[rgb(var(--lm-gold))] text-[rgb(var(--lm-bg))]" data-testid="save-item">
                  {saving ? <Loader2 size={14} className="animate-spin" /> : editingItem ? 'Modifier' : 'Creer'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminMenu;
